// Type definitions for (Expendientes - Folders)

export type SigningStatus = "pending" | "partial" | "signed" | "cancelled";

export interface FolderResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: Folder[];
}

export interface Folder {
  id: string;
  folder_number: number;

  assigned_user: string;
  collaborator: string | null;

  is_active: boolean;
  notes: string | null;
  cja_notes: string | null;
  status: string;

  templates?: Template[];
  subfolders?: Subfolder[];
  created_at: string;
  updated_at: string;

  requirements_summary: Summary;
  contract_ready_at: string | null;
  last_updated_requirement: string;

  analysis: Analysis | null;
  metadata?: Metadata | null;

  is_ready_for_multireport: boolean;
  multireport_ready_at: string | null;
  multireport_sla_target_at: string | null;

  is_multireport_delivered: boolean;
  multireport_delivered_at: string;

  is_lease_contract_generated: boolean;
  lease_contract_generated_at: string;
  contract_sla_target_at: string | null;

  lease_contract_signing_status: SigningStatus;
  lease_contract_sign_date: string;

  is_secure_rent_policy_required: boolean;
  is_ready_for_contract: boolean;

  // MB-362: folio is a contract renewal (same tenant) vs a new contract.
  is_renewal?: boolean;
}

export interface Metadata {
  tenant?: MetadataTenant;
  guarantor?: MetadataGuarantor;
  advisor?: MetadataAdvisor;
  organization?: MetadataOrganization;
  property?: MetadataProperty;
  landlord?: MetadataLandlord;
  rent_policy?: MetadataRentPolicy;
  lease_contract?: MetadataLeaseContract;
  // MB-362: for a renewal folio, the previous contract's folio.
  // MB-379: el backend emite las tres claves juntas (null cuando no aplican) y
  // omite el bloque en un folio ajeno a renovaciones.
  renewal?: {
    // Número del folio de origen, como texto. Lo leen Pipedrive, la herencia
    // de clausulado y esta tarjeta; en el alta manual se teclea.
    previous_folio?: string | null;
    // UUID del folio de origen: permite abrirlo sin traducir antes el número.
    // Puede faltar: renovaciones manuales viejas, o declaradas a mano de un
    // contrato que no está en el sistema, tienen número pero no id.
    previous_id?: string | null;
    // Puntero HACIA ADELANTE. No se guarda en este folio: el backend lo calcula
    // al leer ("qué folio activo me declara como origen"). Como máximo hay una
    // renovación activa, así que es objeto o null, nunca lista.
    renewed_into?: RenewalPointer | null;
  };
}

/** A dónde está la renovación que ya existe: su id y, si se sabe, su folio. */
export interface RenewalPointer {
  id?: string;
  folder_number?: number | string;
}

export interface MetadataRentPolicy {
  status: "approved" | "pre-approved" | "rejected";
  notes: string[];
}

export interface MetadataLeaseContract {
  id: string;
}

export interface MetadataTenant {
  full_name?: string;
  name?: string;
  first_surname?: string;
  second_surname?: string;
  email?: string;
  rfc?: string;
  curp?: string;
  phone_number?: string;
}

export interface MetadataGuarantor {
  full_name?: string;
  name?: string;
  first_surname?: string;
  second_surname?: string;
  email?: string;
  phone_number?: string;
  warning?: {
    count: number;
    has_one_or_more_folders: boolean;
    folder_numbers: number[];
  };
}

export interface MetadataOrganization {
  name?: string;
}

export interface MetadataAdvisor {
  full_name?: string;
  name?: string;
  first_surname?: string;
  second_surname?: string;
  email?: string;
  phone_number?: string;
}

export interface MetadataProperty {
  full_address?: string;
  municipality?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  street?: string;
  exterior_number?: string | number;
  interior_number?: string | number;
  zip_code?: string | number;
}

export interface MetadataLandlord {
  full_name?: string;
  name?: string;
  first_surname?: string;
  second_surname?: string;
  email?: string;
  phone_number?: string;
}

export interface SubfolderMetadata {
  role?: string;
  person_index?: number;
  full_name?: string;
  platform_participant_id?: string;
  // Owner representatives point at the owner subfolder they represent.
  represents_subfolder_id?: string;
  represents_name?: string;
}

export interface Subfolder {
  id: string;
  name: string;
  folder: string;
  status: Status;
  slug: string;
  template: Template;
  requirements: Requirement[];
  metadata?: SubfolderMetadata | null;
}

export interface Template {
  id: string;
  name: string;
  slug: string;
  base_requirements?: string[];
}

export interface Summary {
  pending_upload: number;
  pending_review: number;
  invalid: number;
  valid: number;
}

// Workflow step types for ID and POA validation flows
export type WorkflowStep =
  | "upload"
  | "validating"
  | "address_check"
  | "data_confirmation"
  | "data_edit"
  | "pending_review"
  | "rejected"
  | "completed";

export interface Requirement {
  id: string;
  name: string;
  type: Type;
  status: Status;
  slug: string;
  subtype: null;
  subfolder: string;
  invalid_reason?: string | null;
  files: File[];
  form_values?: FormValue[];
  created_at?: Date;
  updated_at?: Date;
  verification_data?: any;
  workflow_step?: WorkflowStep | null;
  // Server classification of the current run; only explicit false permits legacy controls.
  readonly is_native_bdc_run?: boolean;
  readonly native_bdc_supported?: boolean;
}

export interface File {
  id: string;
  filename: string;
  file_type: string;
  file_path: string;
  size_bytes?: number | null;
  is_verification_reference?: boolean;
  created_at: Date;
}

export interface DeleteFileResponse {
  detail: string;
  deleted_file_ids: string[];
  archived_file_ids: string[];
  restored_file_ids: string[];
  requirement_status: Status | null;
  workflow_step: WorkflowStep | null;
  requirement?: Requirement;
}

export interface FormValue {
  order: number;
  label: string;
  key: string;
  hidden?: boolean;
  required: boolean;
  type: string;
  value: string;
  placeholder: string;
  tooltip?: string;
  separator?: string;
  options: string[] | null;
  prefilled?: boolean; // Field was pre-filled from OCR verification data
}

export interface Requirement {
  id: string;
  name: string;
  type: Type;
}

export enum Type {
  Form = "form",
  Other = "other",
  PhotoID = "photo_id",
  ProofOfAddress = "proof_of_address",
  ProofOfIncome = "proof_of_income",
}

export enum Status {
  Invalid = "invalid",
  PendingReview = "pending_review",
  PendingUpload = "pending_upload",
  Valid = "valid",
}

// action types
export interface OnCreateFolderRequest {
  tenant_name: string;
  tenant_first_surname: string;
  tenant_second_surname?: string;
  tenant_email: string;
  tenant_rfc?: string;
  tenant_curp?: string;
  tenant_phone_number: string;

  advisor_name: string;
  advisor_first_surname: string;
  advisor_second_surname?: string;
  advisor_email: string;
  advisor_phone_number: string;

  landlord_email?: string;
  guarantor_email?: string;
  is_renewal?: boolean;
  previous_folio?: string;
  templates: string[];
}

export interface RequirementsToFilter {
  id: string;
  pending: number;
  toReview: number;
  valid: number;
  invalid: number;
}

export interface OnToggleFolderActiveRequest {
  id: string;
  is_active: boolean;
}

export interface OnAddNotesToFolderRequest {
  id: string;
  notes: string;
}

export interface OnAddCjaNotesToFolderRequest {
  id: string;
  cja_notes: string;
}

export interface FilterDates {
  startDate: string;
  endDate: string;
}

export interface FolderChange {
  name: string;
  role: string;
  action:
    | "created"
    | "deleted"
    | "updated"
    | "status_changed"
    | "assigned"
    | "uploaded";
  author: string;
  status?: {
    actual: string | null;
    previous: string | null;
  };
  resource: string;
  timestamp: string;
  is_active?: {
    actual: boolean | null;
    previous: boolean | null;
  };
  assigned_user?: {
    actual: string | null;
    previous: string | null;
  };
  collaborator?: {
    actual: string | null;
    previous: string | null;
  };
  extra_data?: HistoryExtraData | null;
}

export interface HistoryFieldChange {
  key: string;
  label?: string;
  // Backend mostly sends strings, but values may be number/null/array.
  old_value: string | number | null | unknown[];
  new_value: string | number | null | unknown[];
}

export interface HistoryExtraData {
  prefill?: {
    source: "identity_ocr" | "confirmed_data" | "proof_of_address_ocr";
    skipped: boolean;
    source_requirement_id: string;
    source_requirement_name: string;
  };
  invalid_reason?: string | string[] | null;
  form_edited?: boolean;
  changed_fields?: HistoryFieldChange[];
  changed_field_keys?: string[];
  changed_field_labels?: string[];
  description?: string;
}

export interface HistoryRecord {
  id: string;
  created_at: string;
  resource_type: string;
  resource_id: string;
  resource_slug: string | null;
  name: string;
  action: string;
  author: string;
  role: string;
  previous_status: string | null;
  new_status: string | null;
  previous_is_active: boolean | null;
  new_is_active: boolean | null;
  previous_assigned_user: string | null;
  new_assigned_user: string | null;
  previous_collaborator: string | null;
  new_collaborator: string | null;
  previous_file: string | null;
  new_file: string | null;
  extra_data: HistoryExtraData | null;
}

export interface FolderHistory {
  folder: string;
  changes: FolderChange[];
}

export interface OnReassignUserRequest {
  folder_id: string;
  user_id: string;
}

export interface OnReassignUserResponse {
  new_assigned_user_id: string;
}

export interface OnReassignCollaboratorRequest {
  folder_id: string;
  user_id: string;
}

export interface OnReassignCollaboratorResponse {
  new_collaborator_id: string;
}

export interface OnUpdateAnalysisForm {
  folder_id: string;
  form_values: Analysis;
}

export interface OnRemoveFolderTemplateRequest {
  folder_id: string;
  template_slug: string;
}

export interface OnDownloadFolderAsZipRequest {
  folder_id: string;
  folder_number: number;
}

export interface Analysis {
  valid_id: boolean;
  is_foreign: boolean;
  valid_migration_form: boolean;
  cash_income: boolean;
  tenant_income: boolean;
  other_tenant_income: boolean;
  valid_income: boolean;
  property_under_guarantee: boolean;
  representative_has_admin_permissions: boolean;
  compliance_opinion_positive: boolean;
  has_guarantor: boolean;
  valid_guarantor_id: boolean;
  has_property_under_guarantee: boolean;
  guarantor_is_owner: boolean;
  unencumbered_property: boolean;
  vehicle_under_guarantee: boolean;
  will_register_at_rug: boolean;
  income_month_1: number;
  income_month_2: number;
  income_month_3: number;
  payment_months: number;
  promissory_notes: number;
  has_lease_contract: boolean;
  is_new_boleta_registral: boolean;
  folio_real?: string;
  superficie?: string;
  full_address?: string;
  rented_property_address?: string;
  guarantee_accreditation_type?: "boleta_registral" | "predial" | "escrituras";
}

export interface FilterState {
  count: number;
  next: string | null;
  previous: string | null;
  results: Folder[];
}

export interface FilterUser {
  email: string;
  group: string;
}

export interface CompleteFileFolderResponse {
  code: number;
  message: string;
}
